/**
 * Chart widget data states and chart behaviors' chrome (screenshots/88, 89; §2.14), drawn by DataWidget:
 * empty (names the filter to remove and how many rows that brings back), couldn't load (last good chart dimmed + error id +
 * Retry), too many to draw (top 50 + Others, groupings with few values), still loading / snapshot · reconnecting / Reset zoom
 * chips for the widget header, not for this viewer, and the drill-down breadcrumb. Copy from describeState() and the design.
 */
import { describeState, type DataState } from "../charts/interactions";
import { OvIcon } from "../icons";

/** Centered states that replace the chart at the same size (88 "No rows", "Not for this viewer"). */
export function StateView({ state, onRemove, onEdit }: { state: DataState; onRemove?: () => void; onEdit?: () => void }) {
  const d = describeState(state);
  if (!d.title) return null;
  const icon = state.kind === "empty" ? "funnel-x" : state.kind === "forbidden" ? "lock" : "circle-alert";
  return (
    <div className={`ov-state ov-state--${state.kind}`} role="status">
      <span className="ov-state__icon"><OvIcon name={icon} size={18} /></span>
      <strong className="ov-state__title">{d.title}</strong>
      {d.body && <p className="ov-state__body">{d.body}</p>}
      {state.kind === "empty" && (
        <div className="ov-state__actions">
          {state.removable && onRemove && <button className="btn btn--secondary" onClick={onRemove}><OvIcon name="x" />{d.action}</button>}
          {onEdit && <button className="btn btn--secondary" onClick={onEdit}><OvIcon name="sliders-horizontal" />Edit filters</button>}
        </div>
      )}
    </div>
  );
}

/** 88 "Couldn't load": over the dimmed last good chart. */
export function ErrorBanner({ what, at, id, onRetry }: { what: string; at: string | null; id: string; onRetry: () => void }) {
  return (
    <div className="ov-banner ov-banner--error" role="alert">
      <OvIcon name="circle-alert" size={16} />
      <div><strong>Couldn’t load {what}</strong><p>{at ? `Showing the chart from ${at}. ` : ""}Error {id}.</p></div>
      <button className="btn btn--secondary" onClick={onRetry}><OvIcon name="refresh-cw" />Retry</button>
    </div>
  );
}

/** 88 "Too many to draw": the note, then groupings with few values and "Show as table". */
export function TooBigBanner({ groups, shown, noun, suggestions, onPick }: { groups: number; shown: number; noun: string;
  suggestions: { label: string; groupBy?: string; type?: "table" }[]; onPick: (s: { groupBy?: string; type?: "table" }) => void }) {
  const n = (x: number) => x.toLocaleString("en-US");
  return (
    <div className="ov-toobig">
      <div className="ov-banner ov-banner--warning" role="status">
        <OvIcon name="triangle-alert" size={16} />
        <p>{n(groups)} {noun} is too many bars. Showing the top {shown}; the other {n(groups - shown)} are grouped as Others.</p>
      </div>
      <div className="ov-toobig__chips">
        {suggestions.map((s) => (
          <button key={s.label} className="ov-chip-btn" onClick={() => onPick(s)}>
            <OvIcon name={s.type === "table" ? "table" : s.groupBy === "ward" ? "map-pin" : "tag"} size={13} />{s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Header chips (88, 89). */
export const LoadingChip = ({ ms, onCancel }: { ms: number; onCancel: () => void }) => (
  <span className="ov-head-chip" role="status"><OvIcon name="loader-dots" size={12} />{describeState({ kind: "loading", ms }).title}
    <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={onCancel}>Cancel</button></span>
);
export const OfflineChip = ({ at }: { at: string }) => (
  <span className="ov-head-chip ov-head-chip--offline" role="status"><OvIcon name="wifi-off" size={12} />{describeState({ kind: "offline", snapshotAt: at }).title}</span>
);
export const ResetZoom = ({ range, onReset }: { range: string; onReset: () => void }) => (
  <button type="button" className="ov-head-link" onPointerDown={(e) => e.stopPropagation()} onClick={onReset}><OvIcon name="zoom-in" size={13} />Reset zoom · {range}</button>
);

/** 89 Drill down: ← All alderpersons › Samantha Nugent › by year [Sample split]. Back / Esc goes up a level. */
export function DrillCrumbs({ root, steps, level, sample, onTo }: { root: string; steps: string[]; level: string; sample: boolean; onTo: (depth: number) => void }) {
  return (
    <nav className="ov-crumbs" aria-label="Drill-down path">
      <button type="button" className="ov-crumbs__back" onClick={() => onTo(steps.length - 1)} aria-label="Back one level"><OvIcon name="arrow-left" size={13} /></button>
      <button type="button" className="ov-crumbs__link" onClick={() => onTo(0)}>All {root}</button>
      {steps.map((s, i) => (
        <span key={i} className="ov-crumbs__step"><OvIcon name="chevron-right" size={12} />
          {i === steps.length - 1 ? <strong>{s}</strong> : <button type="button" className="ov-crumbs__link" onClick={() => onTo(i + 1)}>{s}</button>}</span>
      ))}
      <span className="ov-crumbs__step"><OvIcon name="chevron-right" size={12} /><span className="ov-crumbs__level">{level}</span></span>
    </nav>
  );
}
